/**
 * Skeleton loading state for the FormularioAvaliacao component
 * Mirrors the actual form layout to prevent layout shift
 */

import React from 'react'
import { Card, CardContent } from '@/components/ui/Card'
import { Skeleton, SkeletonText, SkeletonRounded } from '@/components/common/Skeleton'

interface FormularioAvaliacaoSkeletonProps {
  /**
   * Which step variant to show
   * - 'config': Initial configuration step (default)
   * - 'category': Category evaluation step with item cards
   * - 'review': Final review step
   */
  variant?: 'config' | 'category' | 'review'
  /**
   * Number of evaluation items to show in category variant
   */
  itemCount?: number
}

/**
 * Shared header skeleton used across all variants
 */
const HeaderSkeleton: React.FC = () => (
  <div className="flex items-center justify-between mb-8">
    <SkeletonRounded width={100} height={36} />
    <div className="flex items-center gap-4">
      <SkeletonText width={140} height={16} />
      <SkeletonRounded width={80} height={24} />
    </div>
  </div>
)

/**
 * Shared step progress indicator skeleton
 */
const StepProgressSkeleton: React.FC<{ activeStep?: number }> = ({ activeStep = 0 }) => (
  <div className="sticky top-0 z-10 bg-brand-dark/95 backdrop-blur-sm border-b border-brand-gray/10 pb-6 pt-4">
    {/* Progress Bar */}
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <SkeletonText width={120} height={16} />
        <SkeletonText width={100} height={16} />
      </div>
      <SkeletonRounded width="100%" height={8} className="rounded-full" />
      <div className="flex justify-center mt-2">
        <SkeletonText width={280} height={12} />
      </div>
    </div>

    {/* Step Dots */}
    <div className="flex items-center justify-between gap-1">
      {Array.from({ length: 11 }).map((_, i) => (
        <Skeleton
          key={i}
          className={`flex-1 h-2 rounded-full ${
            i < activeStep ? 'bg-success/30' : i === activeStep ? 'bg-brand-primary/30' : 'bg-brand-dark-lighter'
          }`}
        />
      ))}
    </div>
  </div>
)

/**
 * Shared navigation footer skeleton
 */
const NavigationFooterSkeleton: React.FC = () => (
  <div className="fixed bottom-0 left-0 right-0 bg-brand-dark/95 backdrop-blur-sm border-t border-brand-gray/10 p-4 z-10">
    <div className="max-w-7xl mx-auto flex items-center justify-between">
      <SkeletonRounded width={110} height={40} />
      <SkeletonRounded width={110} height={40} />
    </div>
  </div>
)

/**
 * Configuration step content skeleton
 */
const ConfigStepSkeleton: React.FC = () => (
  <div className="mt-8 animate-pulse">
    {/* Header */}
    <div className="mb-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary/10 rounded-full mb-4">
        <Skeleton variant="circular" width={32} height={32} />
      </div>
      <div className="flex flex-col items-center gap-2">
        <SkeletonText width={280} height={32} />
        <SkeletonText width={400} height={18} />
      </div>
    </div>

    {/* Form Card */}
    <Card className="bg-brand-dark-light border-brand-gray/10 max-w-2xl mx-auto">
      <CardContent className="p-8 space-y-6">
        {/* Evaluation Type Field */}
        <div className="space-y-3">
          <SkeletonText width={140} height={20} />
          <SkeletonRounded width="100%" height={48} />
        </div>

        {/* Employee Field */}
        <div className="space-y-3">
          <SkeletonText width={180} height={20} />
          <SkeletonRounded width="100%" height={48} />
        </div>

        {/* Period Fields */}
        <div className="space-y-3">
          <SkeletonText width={160} height={20} />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <SkeletonText width={80} height={14} />
              <SkeletonRounded width="100%" height={40} />
            </div>
            <div className="space-y-2">
              <SkeletonText width={60} height={14} />
              <SkeletonRounded width="100%" height={40} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

/**
 * Single evaluation item card skeleton
 */
const EvaluationItemSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <Card className="bg-brand-dark-light border-brand-gray/10">
    <CardContent className="p-8">
      <div className="flex items-start gap-4 mb-6">
        {/* Number badge */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-dark-lighter flex items-center justify-center">
          <span className="text-brand-gray font-bold">{index + 1}</span>
        </div>
        <div className="flex-1">
          {/* Description */}
          <div className="mb-6 space-y-2">
            <SkeletonText width="90%" height={20} />
            <SkeletonText width="70%" height={20} />
          </div>

          {/* Star Rating */}
          <div className="flex items-center justify-center mb-6 py-4 border-y border-brand-gray/10">
            <div className="flex flex-col items-center gap-3">
              <div className="flex gap-2">
                <SkeletonRounded width={40} height={32} /> {/* N/A button */}
                <div className="w-px h-8 bg-brand-gray/20 mx-1" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="circular" width={32} height={32} />
                ))}
              </div>
              <SkeletonText width={120} height={14} />
            </div>
          </div>

          {/* Comments textarea */}
          <div className="space-y-4">
            <div className="space-y-2">
              <SkeletonText width={160} height={14} />
              <SkeletonRounded width="100%" height={100} />
            </div>

            {/* Development plan textarea */}
            <div className="space-y-2">
              <SkeletonText width={180} height={14} />
              <SkeletonRounded width="100%" height={100} />
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
)

/**
 * Category evaluation step content skeleton
 */
const CategoryStepSkeleton: React.FC<{ itemCount: number }> = ({ itemCount }) => (
  <div className="mt-8 animate-pulse">
    {/* Header */}
    <div className="mb-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-blue/10 rounded-full mb-4">
        <Skeleton variant="circular" width={32} height={32} />
      </div>
      <div className="flex flex-col items-center gap-2">
        <SkeletonText width={200} height={32} />
        <SkeletonText width={350} height={18} />
      </div>
      {/* Criteria counter */}
      <div className="mt-4">
        <SkeletonRounded width={200} height={32} className="mx-auto rounded-full" />
      </div>
    </div>

    {/* Evaluation Items */}
    <div className="max-w-4xl mx-auto space-y-6">
      {Array.from({ length: itemCount }).map((_, i) => (
        <EvaluationItemSkeleton key={i} index={i} />
      ))}
    </div>
  </div>
)

/**
 * Review step content skeleton
 */
const ReviewStepSkeleton: React.FC = () => (
  <div className="mt-8 animate-pulse">
    {/* Header */}
    <div className="mb-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary/10 rounded-full mb-4">
        <Skeleton variant="circular" width={32} height={32} />
      </div>
      <div className="flex flex-col items-center gap-2">
        <SkeletonText width={220} height={32} />
        <SkeletonText width={380} height={18} />
      </div>
    </div>

    {/* Overall Score Card */}
    <Card className="bg-brand-dark-light border-brand-primary/20 max-w-2xl mx-auto mb-8">
      <CardContent className="p-8 flex items-center justify-center gap-4">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="space-y-2">
          <SkeletonText width={120} height={14} />
          <SkeletonText width={80} height={48} />
        </div>
      </CardContent>
    </Card>

    {/* Theme Breakdown Cards */}
    <div className="max-w-4xl mx-auto space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SkeletonRounded width={48} height={48} />
                <div className="space-y-2">
                  <SkeletonText width={160} height={20} />
                  <SkeletonText width={120} height={14} />
                </div>
              </div>
              <div className="text-right space-y-1">
                <SkeletonText width={40} height={28} />
                <SkeletonText width={40} height={12} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
)

export const FormularioAvaliacaoSkeleton: React.FC<FormularioAvaliacaoSkeletonProps> = ({
  variant = 'config',
  itemCount = 3
}) => {
  const activeStep = variant === 'config' ? 0 : variant === 'category' ? 2 : 10

  return (
    <div className="min-h-screen bg-brand-dark pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <HeaderSkeleton />
        <StepProgressSkeleton activeStep={activeStep} />

        {variant === 'config' && <ConfigStepSkeleton />}
        {variant === 'category' && <CategoryStepSkeleton itemCount={itemCount} />}
        {variant === 'review' && <ReviewStepSkeleton />}

        <NavigationFooterSkeleton />
      </div>
    </div>
  )
}

export default FormularioAvaliacaoSkeleton
